import {
  AppBase,
  baseFromDoc,
  FirestoreBaseDoc,
  makeDocConverter,
} from '../../base/firestore-model-base'
import type { Timestamp } from 'firebase/firestore'
import type { SchoolType } from './pending-school-account.modl'

/**
 * SchoolAccount（学校アカウント）
 *
 * - `SchoolAccountDoc`: Firestore に保存される形（snake_case / Timestamp）
 * - `SchoolAccount`: アプリで扱う形（camelCase / Date）
 * - `id` / `path` は AppBase で定義（documentId / document は廃止）
 */
export type SchoolAccountDoc = FirestoreBaseDoc & {
  // 学校情報
  school_type: SchoolType
  school_name: string
  postal_code: string
  prefecture: string
  city: string
  address: string
  phone: string
  principal: string
  description?: string | null

  // 学校担当者情報
  contact_name: string
  contact_email: string
  name_kanji: string
  name_kana: string

  // アカウント情報
  email: string
  last_login_at?: Timestamp | null

  // マネーフォワード連携情報
  moneyforward_partner_id?: string | null
  moneyforward_department_id?: string | null

  /** true の学校は請求明細（invoice_items）を作成しない・MF 請求対象外 */
  is_test_school?: boolean
}

export type SchoolAccount = AppBase & {
  // 学校情報
  schoolType: SchoolType
  schoolName: string
  postalCode: string
  prefecture: string
  city: string
  address: string
  phone: string
  principal: string
  description?: string

  // 学校担当者情報
  contactName: string
  contactEmail: string
  nameKanji: string
  nameKana: string

  // アカウント情報
  email: string
  lastLoginAt?: Date

  // マネーフォワード連携情報
  moneyforwardPartnerId?: string
  moneyforwardDepartmentId?: string

  /** テスト学校（請求データを作らない） */
  isTestSchool?: boolean
}

export const schoolAccountDocConverter = makeDocConverter<SchoolAccountDoc>()

export function toSchoolAccount(id: string, doc: SchoolAccountDoc, path: string): SchoolAccount {
  const base = baseFromDoc(id, doc, path)
  return {
    ...base,
    schoolType: doc.school_type,
    schoolName: doc.school_name,
    postalCode: doc.postal_code,
    prefecture: doc.prefecture,
    city: doc.city,
    address: doc.address,
    phone: doc.phone,
    principal: doc.principal,
    description: doc.description ?? undefined,

    contactName: doc.contact_name,
    contactEmail: doc.contact_email,
    nameKanji: doc.name_kanji,
    nameKana: doc.name_kana,

    email: doc.email,
    lastLoginAt: doc.last_login_at ? doc.last_login_at.toDate() : undefined,
    moneyforwardPartnerId: doc.moneyforward_partner_id ?? undefined,
    moneyforwardDepartmentId: doc.moneyforward_department_id ?? undefined,
    isTestSchool: doc.is_test_school === true,
  }
}

